import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useProductEvents } from "@/hooks/use-product-events";
import { useQueryClient } from "@tanstack/react-query";
import {
  useOrderTemplates,
  useIncrementTemplateUsage,
} from "@/hooks/use-order-templates";
import { useApiRequest } from "@/hooks/use-api";
import { useProductQueryCache } from "@/hooks/use-product-query-cache";
import type { Product, SelectedProduct } from "@/types/product";

interface UseTemplateManagementOptions {
  /** URL template parameter for auto-selection. */
  templateId?: string;
  /** URL product parameter for single-product prefill. */
  productId?: string;
  /** Callback to clear URL template parameter. */
  clearTemplateParam?: () => void;
  /** Current number of items in cart (for override confirmation). */
  cartItemCount: number;
  /**
   * Signals that the cart has finished hydrating. URL-based template init
   * waits for this to avoid racing cart hydration and silently wiping
   * a returning user's persistent cart.
   */
  cartReady?: boolean;
  /** Callback to replace cart contents with template products. */
  onApplyProducts: (products: SelectedProduct[]) => void;
}

/**
 * Template management hook.
 *
 * Templates are ACTIONS, not state. Selecting a template is a one-shot
 * operation that seeds the cart. There is no "selected template" — just
 * a record of which template was most recently applied (for analytics
 * at submission time).
 *
 * The hook handles:
 *  - Applying a template: fetches its products and calls onApplyProducts
 *  - Confirmation dialog when cart is non-empty
 *  - Save-as-template dialog orchestration
 *  - URL-based auto-application of a template on initial mount
 *  - Single-product prefill from URL param
 */
export function useTemplateManagement({
  templateId,
  productId,
  clearTemplateParam,
  cartItemCount,
  cartReady = true,
  onApplyProducts,
}: UseTemplateManagementOptions) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { apiRequest } = useApiRequest();
  const { queryKeys, getProductFromCache } = useProductQueryCache();

  const {
    data: templates = [],
    // isPending (not isLoading) so the loading state also covers the window
    // where the query is disabled waiting on Clerk auth/org initialization.
    isPending: templatesLoading,
    isError: templatesError,
  } = useOrderTemplates();
  const incrementTemplateUsage = useIncrementTemplateUsage();
  const { capture } = useProductEvents({ component: "useTemplateManagement" });

  // Last-used template ID — for analytics at submission time, not UI state.
  const lastUsedTemplateIdRef = useRef<string | null>(null);

  // Keep a ref in sync with cartItemCount so applyTemplate can read it without
  // stale closures (cartItemCount is not in applyTemplate's dependency array).
  const cartItemCountRef = useRef(cartItemCount);
  useEffect(() => {
    cartItemCountRef.current = cartItemCount;
  }, [cartItemCount]);

  // Confirmation dialog state
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);

  // Save-as-template dialog state
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);

  // One-time guards for URL-based init
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);
  const [hasAppliedProductPrefill, setHasAppliedProductPrefill] = useState(false);

  /** Fetch full product details by ID — imperatively, using the shared query cache. */
  const fetchProducts = useCallback(
    async (ids: string[]): Promise<Product[]> => {
      if (ids.length === 0) return [];

      return queryClient.fetchQuery<Product[]>({
        queryKey: queryKeys.lookup(ids),
        queryFn: async () => {
          // Check individual product cache first
          const foundMap = new Map<string, Product>();
          const missingIds: string[] = [];
          ids.forEach((id) => {
            const cached = getProductFromCache(id);
            if (cached) foundMap.set(id, cached);
            else missingIds.push(id);
          });

          if (missingIds.length === 0) {
            return ids.map((id) => foundMap.get(id)).filter(Boolean) as Product[];
          }

          const idsParam = missingIds.join(",");
          const response = await apiRequest(
            "GET",
            `/api/products/lookup?ids=${encodeURIComponent(idsParam)}`,
          );
          if (!response.ok) throw new Error("Failed to lookup products");
          const fetched = (await response.json()) as Product[];

          fetched.forEach((p) => foundMap.set(p.uid, p));
          return ids.map((id) => foundMap.get(id)).filter(Boolean) as Product[];
        },
      });
    },
    [queryClient, queryKeys, apiRequest, getProductFromCache],
  );

  /** Apply a template: fetch its products and seed the cart. One-shot. */
  const applyTemplate = useCallback(
    async (tplId: string) => {
      const template = templates.find((t) => t.uid === tplId);
      if (!template) return;

      const pIds = (template.products || [])
        .map((p) => p.productId)
        .filter(Boolean);

      const fullProducts = await fetchProducts(pIds);

      const selectedProducts: SelectedProduct[] = (template.products || [])
        .map((tp) => {
          const fullProduct = fullProducts.find((fp) => fp.uid === tp.productId);
          if (!fullProduct || !fullProduct.isActive) return null;
          if (fullProduct.currentAvailability === "discontinued") return null;
          return {
            ...fullProduct,
            quantity: tp.quantity,
            unitPrice: fullProduct.currentPrice ?? 0,
          } as SelectedProduct;
        })
        .filter((p): p is SelectedProduct => p !== null);

      const replacedCart = cartItemCountRef.current > 0;
      onApplyProducts(selectedProducts);
      lastUsedTemplateIdRef.current = tplId;
      clearTemplateParam?.();
      capture({
        event: "order_template_applied",
        context: {
          templateId: tplId,
          productCount: selectedProducts.length,
          replacedCart,
        },
      });
    },
    [templates, fetchProducts, onApplyProducts, clearTemplateParam],
  );

  /** User clicks a template card or selects from a menu. */
  const selectTemplate = useCallback(
    (tplId: string) => {
      if (tplId === "manage-templates") {
        navigate("/admin/order-templates");
        return;
      }
      if (cartItemCount > 0) {
        setPendingTemplateId(tplId);
        setShowTemplateConfirm(true);
        return;
      }
      applyTemplate(tplId);
    },
    [navigate, cartItemCount, applyTemplate],
  );

  const confirmTemplateReplace = useCallback(() => {
    if (pendingTemplateId) {
      applyTemplate(pendingTemplateId);
    }
    setShowTemplateConfirm(false);
    setPendingTemplateId(null);
  }, [pendingTemplateId, applyTemplate]);

  const saveAndReplaceTemplate = useCallback(() => {
    setShowTemplateConfirm(false);
    setShowSaveTemplateDialog(true);
  }, []);

  const cancelTemplateConfirm = useCallback(() => {
    setShowTemplateConfirm(false);
    setPendingTemplateId(null);
  }, []);

  const handleSaveTemplateDialogClose = useCallback(
    (open: boolean) => {
      setShowSaveTemplateDialog(open);
      if (!open && pendingTemplateId) {
        // User dismissed without saving — don't apply the pending template
        setPendingTemplateId(null);
      }
    },
    [pendingTemplateId],
  );

  const handleTemplateSaved = useCallback(() => {
    setShowSaveTemplateDialog(false);
    if (pendingTemplateId) {
      applyTemplate(pendingTemplateId);
      setPendingTemplateId(null);
    }
  }, [pendingTemplateId, applyTemplate]);

  // URL-based template auto-application.
  // Waits for cartReady so returning users with persistent carts get the
  // confirmation dialog rather than having their cart silently wiped.
  useEffect(() => {
    if (
      templateId &&
      templates.length > 0 &&
      !templatesLoading &&
      cartReady &&
      !hasInitializedFromUrl
    ) {
      const template = templates.find((t) => t.uid === templateId);
      if (template) {
        selectTemplate(templateId);
        setHasInitializedFromUrl(true);
      }
    }
  }, [templateId, templates, templatesLoading, cartReady, selectTemplate, hasInitializedFromUrl]);

  // Single-product prefill from URL param (e.g. "?product=...").
  useEffect(() => {
    if (
      !productId ||
      hasAppliedProductPrefill ||
      !cartReady ||
      cartItemCount > 0 // Don't silently overwrite a non-empty cart
    ) {
      return;
    }

    let cancelled = false;
    (async () => {
      const fetched = await fetchProducts([productId]);
      if (cancelled) return;
      const product = fetched.find(
        (p) =>
          p.uid === productId ||
          p.hajocaProductId === productId ||
          p.sku === productId,
      );
      if (product) {
        onApplyProducts([
          {
            ...product,
            quantity: 1,
            unitPrice: product.currentPrice ?? 0,
          },
        ]);
        setHasAppliedProductPrefill(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productId, hasAppliedProductPrefill, cartReady, cartItemCount, fetchProducts, onApplyProducts]);

  const trackTemplateUsage = useCallback(
    (tplId: string) => {
      incrementTemplateUsage.mutate(tplId);
    },
    [incrementTemplateUsage],
  );

  /** Returns the ID of the most recently applied template, if any. */
  const getLastUsedTemplateId = useCallback(
    () => lastUsedTemplateIdRef.current,
    [],
  );

  return {
    templates,
    templatesLoading,
    templatesError,

    // Core action
    selectTemplate,

    // Analytics
    getLastUsedTemplateId,
    trackTemplateUsage,

    // Confirmation dialog
    showTemplateConfirm,
    pendingTemplateId,
    confirmTemplateReplace,
    saveAndReplaceTemplate,
    cancelTemplateConfirm,

    // Save-as-template dialog
    showSaveTemplateDialog,
    setShowSaveTemplateDialog,
    handleSaveTemplateDialogClose,
    handleTemplateSaved,
  };
}
